/**
 * Ecranul zilnic — livrabilul principal. O zi incape pe un ecran, fara scroll orizontal.
 *
 * Ce raspunde: *Cat am azi, cui platesc, ce marchez?*
 *
 *  - randul de KPI vine din `v_day_position`, deci e la nivel de ZI: pe cele 8 zile cu doua runde
 *    fisierul numara disponibilul de doua ori (D10), aici nu se mai poate;
 *  - taburile de runda inlocuiesc coloanele ad-hoc din foaie (`luni edl`, `marti plati`,
 *    `luni dimi`) fara pierdere — identitatea e `payment_run(zi, seq)` (D1);
 *  - coloana `Facturi` e MEREU VIZIBILA, nu tooltip la hover;
 *  - grila e UN SINGUR tab stop, cu roving tabindex si semantica `role="grid"`;
 *  - editarea e optimista: valoarea apare inainte sa plece cererea, iar la esec RAMANE, cu chip
 *    `Nesalvat`. Un instrument care sterge ce ai scris pentru ca a picat VPN-ul pierde increderea
 *    o data si definitiv.
 *
 * Filtrarea si sortarea traiesc in `filtre.ts`, ca functii pure peste raspunsul rutei: ziua are
 * 12-33 de randuri, deci un filtru nou nu costa un round-trip si grila nu clipeste la fiecare tasta.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import { MessageSquare, Paperclip, Pin } from 'lucide-react';
import { formatDayLabel, formatMinor, plural, weekdayName } from '@evomag/core/format';
import { parseAmountInput } from '@evomag/core/money';
import { cycleStatus } from '@evomag/core/status';
import { Money } from '../../components/Money.tsx';
import { StatusToken } from '../../components/StatusToken.tsx';
import { StatTile } from '../../components/StatTile.tsx';
import { HeroFigure } from '../../components/HeroFigure.tsx';
import { Plus } from 'lucide-react';
import { Legend } from '../../components/Legend.tsx';
import { GolPlanZi } from '../../components/EmptyState.tsx';
import { ErrorState } from '../../components/ErrorState.tsx';
import { SkeletonAnunt, SkeletonRows } from '../../components/SkeletonRows.tsx';
import { CelulaSuma, useCellCursor } from '../grid/index.ts';
import { saptamana, ziLucratoare } from '../../lib/zile.ts';
import { etichetaStare, type CodStare } from '../../lib/stari.ts';
import {
  intrariDinRunda,
  useActiuniBloc,
  usePatchIntrare,
  useRenuntaLaEditare,
  useZi,
  type DayEntryDto,
  type DayRunDto,
} from '../../hooks/useZi.ts';
import { useEditariLocale } from '../../hooks/editari.ts';
import { poateScrie as rolPoateScrie, useReferinta, ziuaLucratoareUrmatoare } from '../../hooks/useReferinta.ts';
import {
  etichetaBanca,
  faraCurs,
  filtreaza,
  grupeaza,
  numaraFiltre,
  optiuniFiltre,
  sorteaza,
} from './filtre.ts';
import { DrawerPlata } from './DrawerPlata.tsx';
import { DialogPlataNoua } from './DialogPlataNoua.tsx';
import { AntetGrila } from './AntetGrila.tsx';
import { ComutatorColoane } from './ComutatorColoane.tsx';
import { CelulaValuta } from './CelulaValuta.tsx';
import { coloaneActive, indexColoana, sablonColoane, type CheieColoana, type DefinitieColoana } from './coloane.ts';
import { CelulaBanca } from './CelulaBanca.tsx';

const api = getRouteApi('/zi/$data');

/**
 * Sablonul CSS, numarul de coloane si indexul coloanei editabile se DERIVA acum din lista de
 * coloane active (`coloane.ts`). Erau trei constante scrise de mana care trebuiau sa ramana
 * sincronizate cu antetul si cu randul — patru surse pentru acelasi adevar, si chiar au divergut.
 */

/** Cheia rundei in URL: eticheta bruta daca exista, altfel numarul de ordine. */
function cheieRunda(r: DayRunDto): string {
  return r.operatorLabel ?? r.labelRaw ?? String(r.seq);
}

export function PlanPlatiZi() {
  const { data } = api.useParams();
  const search = api.useSearch();
  const navigate = api.useNavigate();

  const zi = useZi(data);
  const referinta = useReferinta();
  const patch = usePatchIntrare(data);
  const bloc = useActiuniBloc();
  const renunta = useRenuntaLaEditare();
  // Se aboneaza la magazinul de editari nesalvate; fara asta chip-ul `Nesalvat` n-ar reaparea
  // dupa ce cache-ul revine la adevarul serverului.
  const editariLocale = useEditariLocale();
  const refGrila = useRef<HTMLDivElement>(null);
  const [randCuFocusInainteDeDrawer, setRandCuFocusInainteDeDrawer] = useState<number | null>(null);
  const [adaugaPlata, setAdaugaPlata] = useState(false);

  const poateScrie = rolPoateScrie(referinta.data);
  const selectate = useMemo(() => search.sel ?? [], [search.sel]);
  const idDrawer = search.drawer ?? null;

  const aleRundei = useMemo(() => intrariDinRunda(zi.data, search.runda), [zi.data, search.runda]);

  const optiuni = useMemo(() => optiuniFiltre(zi.data?.entries ?? []), [zi.data]);
  const filtre = useMemo(
    () => ({
      stare: search.stare,
      banca: search.banca,
      valuta: search.valuta,
      categ: search.categ,
      q: search.q,
      min: search.min,
    }),
    [search.stare, search.banca, search.valuta, search.categ, search.q, search.min],
  );
  const filtreActive = numaraFiltre(filtre);

  const randuri = useMemo(
    () => sorteaza(filtreaza(aleRundei, filtre), search.sort),
    [aleRundei, filtre, search.sort],
  );
  const grupuri = useMemo(
    () => grupeaza(randuri, search.grup, etichetaStare as (cod: string) => string),
    [randuri, search.grup],
  );
  const fara = useMemo(() => faraCurs(aleRundei), [aleRundei]);

  // Codurile de banca pentru sugestii. Din nomenclator, nu din ziua curenta: altfel n-ai putea
  // pune INGB pe o zi in care nimeni nu plateste prin ING.
  // Coloana `Valută` se aprinde singura pe zilele care chiar au o plata in alta moneda: pe zilele
  // numai in lei ar fi o coloana de liniute, iar pe cele cu EUR e exact ce trebuie completat.
  const areValuta = useMemo(() => aleRundei.some((x) => x.currency !== 'RON'), [aleRundei]);
  const coloane = useMemo(() => coloaneActive(search.col, areValuta), [search.col, areValuta]);
  const sablon = useMemo(() => sablonColoane(coloane), [coloane]);
  const colSuma = useMemo(() => indexColoana(coloane, 'suma'), [coloane]);
  const colFurnizor = useMemo(() => indexColoana(coloane, 'furnizor'), [coloane]);
  const colFacturi = useMemo(() => indexColoana(coloane, 'facturi'), [coloane]);
  const colBanca = useMemo(() => indexColoana(coloane, 'banca'), [coloane]);

  /**
   * Cursul BNR al zilei, pentru avertismentul de abatere din coloana `Valută`.
   *
   * Se ia din prima plata care are deja un curs de planificare — nu e o referinta perfecta, dar e
   * cea pe care o are ecranul fara inca o cerere, iar `RateInput` o foloseste doar ca sa avertizeze,
   * niciodata ca sa scrie ceva.
   */
  const referintaBnr = useMemo(
    () => aleRundei.find((x) => x.planningRateE6 !== null)?.planningRateE6 ?? null,
    [aleRundei],
  );

  const banciCunoscute = useMemo(
    () =>
      Array.from(
        new Set(
          (referinta.data?.banks ?? [])
            .map((b) => b.ibanBankCode ?? b.code)
            .filter((x): x is string => typeof x === 'string' && x !== ''),
        ),
      ).sort(),
    [referinta.data],
  );

  const kpi = zi.data?.kpi;
  const totalSelectat = randuri
    .filter((e) => selectate.includes(String(e.id)))
    .reduce((s, e) => s + e.amountRonMinor, 0);

  const dupaId = useMemo(() => new Map(randuri.map((e) => [e.id, e])), [randuri]);
  const plataDrawer = idDrawer === null ? null : (dupaId.get(Number(idDrawer)) ?? null);

  // --- navigare in URL -------------------------------------------------------------------------

  const actualizeazaCautarea = useCallback(
    (transforma: (prev: typeof search) => typeof search) => {
      void navigate({ search: transforma, replace: true, resetScroll: false });
    },
    [navigate],
  );

  function deschideDrawer(id: number | null, randCurent?: number): void {
    if (id !== null && typeof randCurent === 'number') setRandCuFocusInainteDeDrawer(randCurent);
    actualizeazaCautarea((prev) => ({ ...prev, drawer: id === null ? undefined : String(id) }));
  }

  function inchideDrawer(): void {
    actualizeazaCautarea((prev) => ({ ...prev, drawer: undefined }));
    // `Esc` readuce focusul pe celula de origine, nu la inceputul paginii.
    if (randCuFocusInainteDeDrawer !== null) cursor.mutaLa(randCuFocusInainteDeDrawer, 0);
    setRandCuFocusInainteDeDrawer(null);
  }

  function comutaFiltru(camp: 'banca' | 'valuta' | 'categ' | 'stare', valoare: string): void {
    actualizeazaCautarea((prev) => {
      const curent = (prev[camp] ?? []) as readonly string[];
      const urmator = curent.includes(valoare)
        ? curent.filter((v) => v !== valoare)
        : [...curent, valoare];
      return { ...prev, [camp]: urmator.length > 0 ? urmator : undefined };
    });
  }

  function stergeFiltrele(): void {
    actualizeazaCautarea((prev) => ({
      ...prev,
      stare: undefined,
      banca: undefined,
      valuta: undefined,
      categ: undefined,
      q: undefined,
      min: undefined,
    }));
  }

  // --- scrieri ---------------------------------------------------------------------------------

  type CorpPatch = Parameters<typeof patch.mutate>[0]['patch'];

  const scrie = useCallback(
    (
      intrare: DayEntryDto,
      patchCorp: CorpPatch,
      brut?: Partial<Record<keyof CorpPatch, string>>,
      optimist?: Partial<DayEntryDto>,
    ) => {
      patch.mutate({
        entryId: intrare.id,
        patch: { version: intrare.version, appVersion: intrare.appVersion, ...patchCorp },
        ...(brut ? { brut } : {}),
        ...(optimist ? { optimist } : {}),
      });
    },
    [patch],
  );

  /**
   * Suma pleaca spre server ca TEXT BRUT (`2.515,67`, `=1000+250`), pentru ca parsarea si
   * rotunjirea au voie sa se intample intr-un singur loc, iar acela e `@evomag/core/money` rulat
   * pe server. Clientul parseaza doar ca sa afiseze imediat — si NU inventeaza o valoare in lei
   * pentru sumele in valuta: acolo cursul e al serverului.
   */
  const scrieSuma = useCallback(
    (intrare: DayEntryDto, text: string) => {
      const parsat = parseAmountInput(text);
      const optimist: Partial<DayEntryDto> | undefined =
        parsat === null
          ? undefined
          : intrare.currency === 'RON'
            ? {
                amountMinor: parsat.minor,
                amountRonMinor: parsat.minor,
                amountExpression: parsat.expression,
              }
            : { amountMinor: parsat.minor, amountExpression: parsat.expression };
      scrie(intrare, { amount: text }, { amount: text }, optimist);
    },
    [scrie],
  );

  /** Cursul, din coloana `Valută`. Serverul recalculeaza echivalentul in lei din el. */
  const scrieCurs = useCallback(
    (intrare: DayEntryDto, rateE6: number | null) => {
      if (rateE6 === null) return;
      scrie(intrare, { rateE6 });
    },
    [scrie],
  );

  /**
   * Suma in LEI, scrisa direct.
   *
   * Sensul invers al conversiei: leii devin cifra autoritara, iar suma in valuta se deriva din
   * curs pe server. Nu se aplica optimist nimic — clientul n-are cursul cu care va calcula
   * serverul, si o cifra inventata pentru 300 ms e mai rea decat o asteptare de 300 ms.
   */
  const scrieSumaLei = useCallback(
    (intrare: DayEntryDto, text: string) => {
      scrie(intrare, { amountRon: text }, { amountRon: text });
    },
    [scrie],
  );

  function setStare(intrare: DayEntryDto, cod: CodStare | null): void {
    if (!poateScrie) return;
    scrie(intrare, { statusOverride: cod });
  }

  // --- masina de cursor ------------------------------------------------------------------------

  const cursor = useCellCursor({
    numarRanduri: randuri.length,
    numarColoane: coloane.length,
    idRanduri: randuri.map((e) => String(e.id)),
    // Indicii vin din lista de coloane, nu din cifre scrise de mana: cand cineva opreste coloana
    // `Facturi`, `Sumă` isi schimba indexul, iar o cifra fixa ar face editabila alta celula.
    esteEditabila: (_r, c) => poateScrie && c === colSuma,
    esteNumerica: (_r, c) => c === colSuma,
    citesteCelula: (r, c) => {
      const e = randuri[r];
      if (!e) return '';
      if (c === colSuma) return formatMinor(e.amountMinor);
      if (c === colFurnizor) return e.supplierName;
      if (c === colFacturi) return e.invoiceRefs.map((f) => f.raw).join(', ');
      if (c === colBanca) return etichetaBanca(e);
      return '';
    },
    laSalvare: (r, c, text) => {
      const e = randuri[r];
      if (!e || c !== colSuma) return;
      // Validarea de forma se face si aici, ca sa nu plece o cerere care sigur intoarce 400 si ca
      // celula sa ramana deschisa cu ce s-a tastat.
      if (parseAmountInput(text) === null) throw new Error(`Nu pot citi suma „${text}”.`);
      scrieSuma(e, text);
    },
    laStergere: (r, c) => {
      const e = randuri[r];
      if (e && c === colSuma) scrieSuma(e, '0');
    },
    laLipire: (la, matrice) => {
      // O coloana lipita din Excel umple in jos, incepand de la cursor. Fiecare rand e o mutatie
      // separata: 12 randuri lipite care esueaza la al 7-lea trebuie sa pastreze primele 6.
      matrice.forEach((rand, i) => {
        const e = randuri[la.rand + i];
        const text = rand[0];
        if (!e || text === undefined || la.col !== colSuma) return;
        if (parseAmountInput(text) !== null) scrieSuma(e, text);
      });
    },
    laCiclareStare: (r, directie) => {
      const e = randuri[r];
      if (e) setStare(e, cycleStatus(e.status, directie));
    },
    laSetareStare: (r, cod) => {
      const e = randuri[r];
      if (e) setStare(e, cod as CodStare);
    },
    laDeschidere: (_ce, r) => {
      const e = randuri[r];
      if (e) deschideDrawer(e.id, r);
    },
    laAmanare: (ids) => {
      if (!poateScrie || ids.length === 0) return;
      bloc.mutate({
        data,
        actiune: {
          action: 'postpone',
          entryIds: ids.map(Number),
          mode: 'auto_next_business_day',
        },
      });
    },
    laNavigareZi: (delta) => {
      void navigate({
        params: { data: ziLucratoare(data, delta > 0 ? 1 : -1, referinta.data?.calendar) },
        search: (prev) => prev,
      });
    },
    laNavigareSaptamana: (delta) => {
      void navigate({ params: { data: saptamana(data, delta > 0 ? 1 : -1) }, search: (prev) => prev });
    },
    selectieControlata: selectate,
    laSchimbareSelectie: (ids) => {
      actualizeazaCautarea((prev) => ({ ...prev, sel: ids.length > 0 ? [...ids] : undefined }));
    },
    // Headerul grilei si bara de actiuni sunt sticky: fara insetul asta, celula focusata ar
    // ajunge exact sub ele (WCAG 2.2 SC 2.4.11).
    zonaSigura: { sus: 36, jos: 64 },
  });

  // --- randare ---------------------------------------------------------------------------------

  if (zi.isError) {
    return (
      <div className="p-4">
        <ErrorState
          titlu={`Nu am putut încărca ziua ${formatDayLabel(data)}`}
          detaliu={zi.error.message}
          onReincearca={() => void zi.refetch()}
        />
      </div>
    );
  }

  const runde = zi.data?.runs ?? [];
  const rundaCurenta = search.runda ?? (runde[0] ? cheieRunda(runde[0]) : undefined);
  // Runda in care aterizeaza o plata noua: cea selectata in tabul de runde, altfel prima a zilei.
  // `null` cand ziua nu are nicio runda — atunci o creeaza serverul, in acelasi batch cu plata.
  const rundaTinta = runde.find((r) => cheieRunda(r) === rundaCurenta) ?? runde[0] ?? null;

  return (
    <div className="flex min-h-full">
      <div className="flex min-w-0 grow flex-col gap-4 p-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-20">Plan de plăți · {zi.data?.dayLabel ?? formatDayLabel(data)}</h1>
            <p className="text-12 text-ink-2 italic">Cât am azi, cui plătesc, ce marchez?</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {kpi && !kpi.isBusinessDay ? (
              <span className="badge" title={kpi.holidayName ?? 'Zi nelucrătoare'}>
                {kpi.isPublicHoliday ? (kpi.holidayName ?? 'Sărbătoare legală') : 'Zi nelucrătoare'}
              </span>
            ) : null}
            {poateScrie ? (
              <button
                type="button"
                className="hit-24 chip font-medium text-ink-1"
                onClick={() => setAdaugaPlata(true)}
              >
                <Plus size={12} aria-hidden="true" />
                Adaugă o plată
              </button>
            ) : null}
            <ComutatorColoane
              active={coloane}
              alese={search.col}
              onSchimba={(chei) =>
                actualizeazaCautarea((prev) => ({ ...prev, col: chei as string[] | undefined }))
              }
            />
            <Link to="/matrice" search={{ ancora: data }} className="hit-24 chip">
              Vezi matricea
            </Link>
            <Link to="/valutar/$data" params={{ data }} className="hit-24 chip">
              Comparator curs
            </Link>
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            eticheta="Disponibil"
            minor={kpi?.disponibilMinor ?? null}
            detaliu="Lei + valută, la nivel de zi"
            actiune={
              <Link
                to="/cashflow"
                search={{ de: data, pana: data }}
                className="chip"
                title="Proveniența cifrei: pozițiile de trezorerie ale zilei"
              >
                din Cashflow
              </Link>
            }
          />
          <StatTile
            eticheta="De plată"
            minor={kpi?.dePlataMinor ?? null}
            detaliu={`${plural(kpi?.entryCount ?? 0, 'plată', 'plăți', 'de plăți')} în ${plural(kpi?.runCount ?? 0, 'rundă', 'runde', 'de runde')}, fără cele blocate`}
          />
          <StatTile
            eticheta="Blocat TVA"
            minor={kpi?.blocatMinor ?? null}
            detaliu="Scalar per zi; scara de formule din fișier a dispărut"
          />
          <HeroFigure
            minor={kpi?.dupaPlataMinor ?? 0}
            consumat={(kpi?.dePlataMinor ?? 0) + (kpi?.blocatMinor ?? 0)}
            disponibil={kpi?.disponibilMinor ?? 0}
            detaliu="Disponibil − de plată − blocat, calculat la nivel de zi"
          />
        </div>

        {/* Platile fara curs nu intra in `De plata`, dar se numara pe o linie proprie. */}
        {fara.length > 0 ? (
          <p className="text-12 text-ink-2" role="status">
            <span data-stare="waiting" className="pastila">
              Fără curs ({fara.length})
            </span>{' '}
            {plural(fara.length, 'plată în valută nu are curs', 'plăți în valută nu au curs', 'de plăți în valută nu au curs')}{' '}
            și nu intră în total.{' '}
            <Link to="/valutar/$data" params={{ data }} className="underline">
              Completează cursurile
            </Link>
          </p>
        ) : null}

        {/* Taburile de runda inlocuiesc coloanele ad-hoc, fara pierdere. */}
        {runde.length > 0 ? (
          <div role="tablist" aria-label="Runde de plată" className="flex flex-wrap items-center gap-1">
            {runde.map((r) => {
              const cheie = cheieRunda(r);
              const activ = cheie === rundaCurenta;
              return (
                <button
                  key={r.runId}
                  type="button"
                  role="tab"
                  aria-selected={activ}
                  className={`hit-24 chip ${activ ? 'font-medium text-ink-1' : ''}`}
                  style={activ ? { borderColor: 'var(--select-line)' } : undefined}
                  onClick={() => actualizeazaCautarea((prev) => ({ ...prev, runda: cheie, sel: undefined }))}
                >
                  {r.operatorLabel ?? r.labelRaw}
                  <span className="num text-ink-3">#{r.seq}</span>
                  {r.entryCount !== null ? (
                    <span className="num text-ink-3">· {r.entryCount}</span>
                  ) : null}
                  {r.legalEntity !== 'EVO' ? (
                    <span
                      className="badge"
                      title={
                        r.legalEntity === 'EDL'
                          ? 'Entitate juridică separată: EVO DISTRIBUTION LINE'
                          : `Entitate: ${r.legalEntity}`
                      }
                    >
                      {r.legalEntity}
                    </span>
                  ) : null}
                  {r.isClosed ? <span className="badge">închisă</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}

        <BaraFiltre
          optiuni={optiuni}
          search={search}
          filtreActive={filtreActive}
          onComuta={comutaFiltru}
          onSchimbaText={(camp, valoare) =>
            actualizeazaCautarea((prev) => ({ ...prev, [camp]: valoare }))
          }
          onSterge={stergeFiltrele}
        />

        <Legend />

        {zi.isPending ? (
          <>
            <SkeletonAnunt />
            <SkeletonRows />
          </>
        ) : randuri.length === 0 ? (
          <GolPlanZi
            dataIso={data}
            filtreActive={filtreActive}
            onStergeFiltre={stergeFiltrele}
            onAdauga={() => setAdaugaPlata(true)}
            onCopiazaZiPrecedenta={() =>
              void navigate({
                params: { data: ziLucratoare(data, -1, referinta.data?.calendar) },
                search: (prev) => prev,
              })
            }
            onVeziMatrice={() => void navigate({ to: '/matrice', search: { ancora: data } })}
          />
        ) : (
          <div
            ref={refGrila}
            data-grid-scroll
            aria-label={`Plăți pentru ${formatDayLabel(data)}${rundaCurenta ? `, runda ${rundaCurenta}` : ''}`}
            aria-rowcount={randuri.length + 1}
            aria-colcount={coloane.length}
            className="grila overflow-auto rounded-[var(--radius-lg)] border border-hairline"
            {...cursor.proprietatiGrila}
          >
            <AntetGrila
              sablon={sablon}
              coloane={coloane}
              sort={search.sort}
              onSort={(cheie) =>
                actualizeazaCautarea((prev) => ({
                  ...prev,
                  sort: prev.sort === cheie ? undefined : cheie,
                }))
              }
            />

            {grupuri.map((grup) => (
              <div key={grup.cheie} role="rowgroup">
                {grup.eticheta !== '' ? (
                  <div className="flex items-center justify-between gap-2 border-b border-hairline bg-sticky px-2 py-1 text-11 text-ink-2">
                    <span>
                      {grup.eticheta}{' '}
                      <span className="num text-ink-3">({grup.intrari.length})</span>
                    </span>
                    <Money minor={grup.totalRonMinor} className="text-11" />
                  </div>
                ) : null}

                {grup.intrari.map((e) => (
                  <RandPlata
                    key={e.id}
                    intrare={e}
                    indice={randuri.indexOf(e)}
                    data={data}
                    cursor={cursor}
                    selectat={selectate.includes(String(e.id))}
                    editari={editariLocale}
                    poateScrie={poateScrie}
                    banciCunoscute={banciCunoscute}
                    coloane={coloane}
                    sablon={sablon}
                    referintaBnr={referintaBnr}
                    onCurs={scrieCurs}
                    onSumaLei={scrieSumaLei}
                    onDeschide={(r) => deschideDrawer(e.id, r)}
                    onRenunta={() => renunta(e.id, 'amount')}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Sectiune fixata jos: randul amanat NU dispare din ziua din care a plecat. */}
        {(zi.data?.postponedToday.length ?? 0) > 0 ? (
          <section className="flex flex-col gap-1 rounded-[var(--radius-lg)] border border-hairline p-3">
            <h2 className="text-13">
              Amânate din azi{' '}
              <span className="num text-ink-3">({zi.data?.postponedToday.length})</span>
            </h2>
            <ul className="flex flex-col gap-1 text-12">
              {zi.data?.postponedToday.map((p) => (
                <li key={p.entryId} className="flex flex-wrap items-baseline gap-2">
                  <span className="truncate">{p.supplierName}</span>
                  <Money minor={p.amountRonMinor} className="text-12" />
                  <span className="text-ink-3" aria-hidden="true">
                    →
                  </span>
                  <Link
                    to="/zi/$data"
                    params={{ data: p.targetDate }}
                    search={{ drawer: String(p.toEntryId) }}
                    className="num underline"
                  >
                    {formatDayLabel(p.targetDate)}
                  </Link>
                  {p.wasAutomatic ? <span className="badge">automat</span> : null}
                  {p.reason ? <span className="text-ink-3">„{p.reason}”</span> : null}
                  {p.hopIndex >= 3 ? (
                    <span className="badge num" title="Lanț lung de amânări">
                      ↻{p.hopIndex}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="text-11 text-ink-3">
              Data scadenței nu se suprascrie niciodată; se mută doar data plății planificate.
            </p>
          </section>
        ) : null}

        {selectate.length > 0 ? (
          <div
            className="bulkbar sticky bottom-4 flex flex-wrap items-center gap-3 px-3 py-2"
            role="region"
            aria-label="Acțiuni pentru selecție"
          >
            {/* AGREGATUL PRIMUL: cifra e motivul pentru care selectezi. */}
            <span className="text-13 font-medium">
              <span className="num">{selectate.length}</span> selectate ·{' '}
              <Money minor={totalSelectat} className="text-13" />
            </span>
            <button
              type="button"
              className="hit-24 chip font-medium text-ink-1"
              disabled={!poateScrie || bloc.isPending}
              onClick={() =>
                bloc.mutate({
                  data,
                  actiune: {
                    action: 'status',
                    entryIds: selectate.map(Number),
                    status: 'approved',
                  },
                })
              }
            >
              Marchează aprobat (A)
            </button>
            <button
              type="button"
              className="hit-24 chip"
              disabled={!poateScrie || bloc.isPending}
              onClick={() =>
                bloc.mutate({
                  data,
                  actiune: {
                    action: 'postpone',
                    entryIds: selectate.map(Number),
                    mode: 'auto_next_business_day',
                  },
                })
              }
            >
              Amână selecția (Ctrl+Shift+P)
            </button>
            <button
              type="button"
              className="hit-24 chip"
              onClick={() => actualizeazaCautarea((prev) => ({ ...prev, sel: undefined }))}
            >
              Golește selecția
            </button>
            {bloc.isError ? (
              <span className="text-11 text-neg" role="alert">
                {bloc.error.message}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {adaugaPlata ? (
        <DialogPlataNoua
          data={data}
          runId={rundaTinta?.runId ?? null}
          etichetaRunda={rundaTinta ? (rundaTinta.operatorLabel ?? rundaTinta.labelRaw) : null}
          valute={referinta.data?.currencies ?? []}
          onClose={() => setAdaugaPlata(false)}
          // Plata noua se deschide imediat in drawer: dupa ce ai adaugat-o, urmatorul lucru pe care
          // vrei sa-l faci e sa-i pui un comentariu sau o factura.
          onCreat={(entryId) => deschideDrawer(entryId)}
        />
      ) : null}

      {plataDrawer ? (
        <DrawerPlata
          plata={plataDrawer}
          data={data}
          ziuaUrmatoare={ziuaLucratoareUrmatoare(referinta.data, data)}
          poateScrie={poateScrie}
          onClose={inchideDrawer}
          onSchimbaSuma={(minor, expresie) =>
            scrieSuma(plataDrawer, expresie ?? formatMinor(minor))
          }
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Randul de plata
// ---------------------------------------------------------------------------

interface PropsRand {
  intrare: DayEntryDto;
  indice: number;
  data: string;
  /** Codurile de banca cunoscute, pentru sugestiile din celula editabila. */
  banciCunoscute: readonly string[];
  coloane: readonly DefinitieColoana[];
  sablon: string;
  /** Cursul BNR al zilei, pentru avertismentul de abatere din coloana `Valută`. */
  referintaBnr: number | null;
  onCurs: (intrare: DayEntryDto, rateE6: number | null) => void;
  onSumaLei: (intrare: DayEntryDto, text: string) => void;
  cursor: ReturnType<typeof useCellCursor>;
  selectat: boolean;
  editari: readonly { zona: string; id: number; camp: string; valoare: string; stare: string }[];
  poateScrie: boolean;
  onDeschide: (rand: number) => void;
  onRenunta: () => void;
}

function RandPlata({
  intrare,
  indice,
  data,
  banciCunoscute,
  coloane,
  sablon,
  referintaBnr,
  onCurs,
  onSumaLei,
  cursor,
  selectat,
  editari,
  poateScrie,
  onDeschide,
  onRenunta,
}: PropsRand) {
  const e = intrare;
  const nesalvat = editari.find(
    (x) => x.zona === 'intrare' && x.id === e.id && x.camp === 'amount',
  );
  const colSuma = coloane.findIndex((c) => c.cheie === 'suma');
  const seEditeaza = cursor.editare?.rand === indice && cursor.editare.col === colSuma;
  const areEroare = cursor.eroare?.rand === indice && cursor.eroare.col === colSuma;

  const numeAccesibil = [
    e.supplierName,
    weekdayName(data),
    `${formatMinor(e.amountMinor)} ${e.currency === 'RON' ? 'lei' : e.currency}`,
    etichetaStare(e.status),
    e.invoiceRefs.length > 0
      ? `facturi ${e.invoiceRefs.map((f) => f.raw).join(', ')}`
      : 'fără facturi',
    e.attachmentCount > 0
      ? plural(e.attachmentCount, 'atașament', 'atașamente', 'de atașamente')
      : 'fără atașamente',
    e.hasSupplierRule ? 'are regulă de furnizor' : '',
    e.postponeCount > 0 ? `amânat de ${e.postponeCount} ori` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  /**
   * Continutul unei celule, dupa cheia coloanei.
   *
   * Randul se genereaza din ACEEASI lista ca antetul, deci nu mai pot diverge. Inainte erau sapte
   * `<span>`-uri scrise de mana in fiecare loc, cu indici de cursor scrisi ca cifre — si chiar au
   * divergut: antetul aluneca o coloana la stanga.
   */
  function continutCelula(cheie: CheieColoana) {
    switch (cheie) {
      case 'selectie':
        return (
          // `hit-24` sta pe `<label>`, nu pe `<input>`: pseudo-elementele nu se randeaza pe
          // elemente inlocuite, deci pe checkbox extinderea zonei n-ar exista.
          <label className="hit-24 inline-flex items-center">
            <span className="doar-sr">Selectează {e.supplierName}</span>
            <input
              type="checkbox"
              checked={selectat}
              tabIndex={-1}
              onChange={() => cursor.comutaSelectia(String(e.id))}
            />
          </label>
        );

      case 'stare':
        return <StatusToken stare={e.status} varianta="complet" />;

      case 'furnizor':
        return (
          <>
            <span className="truncate" title={e.supplierName}>
              {e.supplierName}
            </span>
            {/* Regula de furnizor e vizibila pe FIECARE rand al furnizorului, in orice grila, si
                intra in numele accesibil al randului. */}
            {e.hasSupplierRule ? (
              <Pin size={12} aria-hidden="true" className="shrink-0 text-ink-3" />
            ) : null}
            {e.postponeCount > 0 ? (
              <span className="badge num" title={`Amânat de ${e.postponeCount} ori`}>
                ↻{e.postponeCount}
              </span>
            ) : null}
            {e.needsAttention ? (
              <span className="badge" title={e.attentionReason ?? 'De verificat'}>
                !
              </span>
            ) : null}
          </>
        );

      case 'suma':
        return (
          <>
            <CelulaSuma
              minor={e.amountMinor}
              moneda={e.currency === 'RON' ? 'lei' : e.currency.toLowerCase()}
              expresie={e.amountExpression}
              eticheta={`Sumă pentru ${e.supplierName}, ${formatDayLabel(data)}`}
              seEditeaza={seEditeaza}
              textEditare={cursor.editare?.text ?? ''}
              laFinal={cursor.editare?.dinTastare ?? false}
              laSchimbare={cursor.schimbaTextul}
              laBlur={() => void cursor.salveaza('ramai')}
              stareSalvare={areEroare || nesalvat ? 'eroare' : 'curat'}
              mesajEroare={cursor.eroare?.mesaj ?? null}
            />
            {e.fxTransactionId !== null ? (
              <span className="chip ml-1" title="Sumă derivată dintr-o tranzacție valutară">
                FX
              </span>
            ) : null}
            {/* La esec valoarea RAMANE, langa ea chip-ul si reincercarea. */}
            {nesalvat && poateScrie ? (
              <span className="ml-1 inline-flex items-center gap-1">
                <span data-stare="urgent" className="pastila" title={nesalvat.valoare}>
                  {nesalvat.stare === 'conflict' ? 'Conflict' : 'Nesalvat'}
                </span>
                <button type="button" className="hit-24 chip" tabIndex={-1} onClick={onRenunta}>
                  Renunță
                </button>
              </span>
            ) : null}
          </>
        );

      case 'valuta':
        return (
          <CelulaValuta
            intrare={e}
            poateScrie={poateScrie}
            referintaBnr={referintaBnr}
            onCurs={(rateE6) => onCurs(e, rateE6)}
            onSumaLei={(text) => onSumaLei(e, text)}
          />
        );

      case 'sold':
        return <Money minor={e.soldMinor} moneda={null} zecimale={0} className="text-ink-2" />;

      case 'stoc':
        // Rosu cand stocul depaseste soldul: „nu se plateste marfa nevanduta" e regula pe care o
        // aplica in fiecare zi. `null` inseamna „nu exista snapshot", nu „e in regula".
        return (
          <Money
            minor={e.stocMinor}
            moneda={null}
            zecimale={0}
            className={e.stocGtSold === true ? 'text-neg' : 'text-ink-2'}
          />
        );

      case 'facturi':
        return e.invoiceRefs.length === 0 ? (
          <span className="text-ink-3">—</span>
        ) : (
          e.invoiceRefs.map((f) => (
            <span
              key={f.id}
              className="chip num"
              title={f.confidence === 'inferred' ? 'Extrasă automat din comentariu' : f.raw}
            >
              {f.raw}
            </span>
          ))
        );

      case 'banca':
        return <CelulaBanca intrare={e} banciCunoscute={banciCunoscute} poateScrie={poateScrie} />;

      case 'note':
        return (
          <>
            {e.commentCount > 0 ? (
              <span
                className="inline-flex items-center gap-0.5 text-11 text-ink-2"
                title={`${e.commentCount} comentarii, ${e.unresolvedCommentCount} nerezolvate`}
              >
                <MessageSquare size={12} aria-hidden="true" />
                <span className="num">{e.commentCount}</span>
              </span>
            ) : null}
            {e.attachmentCount > 0 ? (
              <span className="inline-flex items-center gap-0.5 text-11 text-ink-2">
                <Paperclip size={12} aria-hidden="true" />
                <span className="num">{e.attachmentCount}</span>
              </span>
            ) : null}
            <button
              type="button"
              className="hit-24 chip"
              tabIndex={-1}
              aria-label={`Deschide detaliile pentru ${e.supplierName}`}
              onClick={() => onDeschide(indice)}
            >
              Detalii
            </button>
          </>
        );

      default:
        return null;
    }
  }

  /** Clasele proprii fiecarei celule; alinierea vine din definitia coloanei, ca la antet. */
  const CLASE: Partial<Record<CheieColoana, string>> = {
    selectie: 'flex items-center justify-center',
    furnizor: 'flex min-w-0 items-center gap-2',
    facturi: 'flex min-w-0 flex-wrap gap-1',
    banca: 'min-w-0',
    note: 'flex items-center justify-end gap-2',
  };

  return (
    <div
      role="row"
      aria-rowindex={indice + 2}
      aria-selected={selectat}
      aria-label={numeAccesibil}
      data-stare={e.status}
      data-rand-grila
      className="rand-plata grid items-center"
      style={{ gridTemplateColumns: sablon }}
    >
      {coloane.map((col, i) => (
        <span
          key={col.cheie}
          role="gridcell"
          className={`celula focus-interior ${col.aliniere ?? ''} ${CLASE[col.cheie] ?? ''}`}
          {...cursor.proprietatiCelula(indice, i)}
        >
          {continutCelula(col.cheie)}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bara de filtre
// ---------------------------------------------------------------------------

interface PropsBaraFiltre {
  optiuni: { banci: readonly string[]; valute: readonly string[]; categorii: readonly string[] };
  search: ReturnType<typeof api.useSearch>;
  filtreActive: number;
  onComuta: (camp: 'banca' | 'valuta' | 'categ' | 'stare', valoare: string) => void;
  onSchimbaText: (camp: 'q', valoare: string | undefined) => void;
  onSterge: () => void;
}

function BaraFiltre({ optiuni, search, filtreActive, onComuta, onSchimbaText, onSterge }: PropsBaraFiltre) {
  return (
    <div className="flex flex-wrap items-center gap-2" role="search">
      <label className="flex items-center gap-1">
        <span className="doar-sr">Caută furnizor, CUI sau factură</span>
        <input
          type="search"
          className="input"
          placeholder="Furnizor, CUI, factură…"
          value={search.q ?? ''}
          onChange={(ev) => onSchimbaText('q', ev.target.value === '' ? undefined : ev.target.value)}
        />
      </label>

      {optiuni.valute.length > 1
        ? optiuni.valute.map((v) => (
            <button
              key={v}
              type="button"
              className={`hit-24 chip ${search.valuta?.includes(v as never) ? 'font-medium text-ink-1' : ''}`}
              aria-pressed={search.valuta?.includes(v as never) ?? false}
              onClick={() => onComuta('valuta', v)}
            >
              {v}
            </button>
          ))
        : null}

      {optiuni.banci.length > 1 ? (
        <label className="flex items-center gap-1 text-12">
          <span className="text-ink-2">Bancă</span>
          <select
            className="input"
            value=""
            onChange={(ev) => {
              if (ev.target.value !== '') onComuta('banca', ev.target.value);
              ev.target.value = '';
            }}
          >
            <option value="">toate</option>
            {optiuni.banci.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {(search.banca ?? []).map((b: string) => (
        <button
          key={b}
          type="button"
          className="hit-24 chip font-medium text-ink-1"
          onClick={() => onComuta('banca', b)}
          aria-label={`Elimină filtrul de bancă ${b}`}
        >
          {b} ×
        </button>
      ))}
      {(search.categ ?? []).map((c: string) => (
        <button
          key={c}
          type="button"
          className="hit-24 chip font-medium text-ink-1"
          onClick={() => onComuta('categ', c)}
          aria-label={`Elimină filtrul de categorie ${c}`}
        >
          {c} ×
        </button>
      ))}

      {filtreActive > 0 ? (
        <button type="button" className="hit-24 chip" onClick={onSterge}>
          Șterge filtrele ({filtreActive})
        </button>
      ) : null}
    </div>
  );
}
